"use client";

import React from "react";
import { TASTING_MENUS, TastingMenuItem } from "@/lib/demos/restaurantData";
import { Utensils, Wine, Sparkles, Check, ArrowRight } from "lucide-react";

interface TastingMenuSectionProps {
  currency: "INR" | "USD";
  onSelectMenu: (menuId: string) => void;
}

export default function TastingMenuSection({
  currency,
  onSelectMenu,
}: TastingMenuSectionProps) {
  return (
    <section className="py-16 bg-stone-900/60 border-b border-stone-800 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Title */}
        <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Seasonal Culinary Progressions</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
            Curated Tasting Menus
          </h2>
          <p className="text-sm sm:text-base text-stone-300">
            Each menu is a choreographed multi-course symphony crafted with hyper-seasonal ingredients, wood-fired techniques, and rare cellar pairings.
          </p>
        </div>

        {/* Menus Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {TASTING_MENUS.map((menu: TastingMenuItem) => {
            const priceDisplay = currency === "INR" ? menu.priceINR : menu.priceUSD;
            const pairingDisplay = currency === "INR" ? menu.pairingPriceINR : menu.pairingPriceUSD;

            return (
              <div
                key={menu.id}
                className="bg-stone-950 rounded-2xl border border-stone-800 hover:border-amber-500/40 transition-all duration-300 flex flex-col overflow-hidden group shadow-xl hover:shadow-amber-500/10 hover:-translate-y-1"
              >
                {/* Image & Badge */}
                <div className="relative h-52 overflow-hidden bg-stone-900">
                  <img
                    src={menu.image}
                    alt={menu.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 brightness-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/20 to-transparent" />
                  
                  <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-md px-2.5 py-1 rounded-full border border-amber-500/30 text-[11px] font-bold text-amber-300">
                    {menu.badge}
                  </div>

                  <div className="absolute bottom-3 left-3">
                    <span className="text-xs uppercase tracking-wider font-semibold text-stone-400 block">
                      {menu.courses} Choreographed Courses
                    </span>
                    <span className="text-xl font-extrabold text-amber-400">
                      {priceDisplay}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col justify-between space-y-6">
                  <div className="space-y-3">
                    <h3 className="text-lg font-bold text-white group-hover:text-amber-300 transition-colors">
                      {menu.name}
                    </h3>
                    <p className="text-xs text-stone-400 leading-relaxed">
                      {menu.description}
                    </p>

                    {/* Highlight Dishes */}
                    <div className="pt-2 space-y-2 border-t border-stone-800/80">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-amber-400/90 block">
                        Signature Highlights:
                      </span>
                      <ul className="space-y-1.5">
                        {menu.highlightDishes.map((dish, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-stone-300">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                            <span>{dish}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Sommelier Pairing & CTA */}
                  <div className="space-y-4 pt-4 border-t border-stone-800/80">
                    <div className="flex items-center justify-between text-xs bg-stone-900/90 px-3 py-2 rounded-lg border border-stone-800">
                      <div className="flex items-center gap-1.5 text-stone-300">
                        <Wine className="w-3.5 h-3.5 text-amber-400" />
                        <span>Optional Sommelier Flight:</span>
                      </div>
                      <span className="font-semibold text-amber-300">{pairingDisplay}</span>
                    </div>

                    <button
                      onClick={() => onSelectMenu(menu.id)}
                      className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold text-xs shadow-md shadow-amber-600/20 transition-all flex items-center justify-center gap-2 group/btn"
                    >
                      <span>Reserve This Tasting Menu</span>
                      <ArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
